import { TabViewAccess } from '@brighthub/admin-contract';
import {
  ApplicationConfig,
  computed,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { appRoutes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideBDSTheme } from '@brightside/angular-bds';
import {
  AuthService,
  provideAppAuth,
  provideOktaAuth,
} from '@brightside/core/auth';
import { environment } from '@brighthub/environment';
import { providePluginExecution } from '@brighthub/plugin';
import { BrightnavModule } from '@brighthub/brightnav';
import { BrightnavAdminModule } from '@brighthub/brightnav-admin';
import { AskNaviModule } from '@brighthub/ask-navi';
import { provideHttpClient } from '@angular/common/http';
import { CopilotFacadeService, provideCopilot } from '@brighthub/copilot';
import { provideReviewTab } from '@brightnav/review';
import { provideOpenActions } from '@brightnav/open-actions';
import { provideFeatureFlags } from '@brighthub/feature-flags';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { MessageService } from 'primeng/api';
import { MarkdownModule } from 'ngx-markdown';
// APP ENTRY MODULES NEED TO COME FIRST FOR MENU REGISTRATION
import { MARKED_OPTIONS } from 'ngx-markdown';
import { SentryService, provideSentryAngular } from '@brighthub/logging';
import {
  ClientSearchService,
  MemberMetadataHeaderService,
} from '@brightnav/ui';
import { ClientSessionService } from '@brighthub/uisession';
import { FeedbackService } from '@brighthub/feedback';
import { provideAccounts } from '@brightnav/accounts';
import { provideFinancialSnapshot } from '@brightnav/financial-snapshot';
import {
  ACTION_EMPLOYEE_SEARCH_API,
  CARE_PLAN_ACCESS,
  CARE_PLAN_CLIENT_CONTEXT,
  CARE_PLAN_CURRENT_USER,
  CARE_PLAN_NAVI,
  CARE_PLAN_EDIT_GROUP,
  CARE_PLAN_LOWER_ENV_ADMIN_GROUP,
  canOverrideOptionQualification,
  provideCarePlan,
} from '@brightnav/care-plan';
import { provideCreditOverview } from '@brightnav/credit-overview';
import { EmployeeSearchService } from '@brighthub/brightnav-employee-search';
import { markedOptionsFactory } from './markdown.config';

export const appConfig: ApplicationConfig = {
  providers: [
    provideSentryAngular(),
    SentryService,
    provideAppInitializer(() => {
      inject(SentryService);
    }),
    ...BrightnavModule,
    ...BrightnavAdminModule,
    ...AskNaviModule,
    importProvidersFrom([
      MarkdownModule.forRoot({
        markedOptions: {
          provide: MARKED_OPTIONS,
          useFactory: markedOptionsFactory,
        },
      }),
    ]),
    MessageService,
    provideHttpClient(),
    // Global NgRx store; feature state (e.g. Copilot) is registered via provideCopilot() and other providers.
    // Routes and feature providers (e.g. BrightnavModule) register their own routes and providers so the root config stays minimal.
    provideStore(),
    provideEffects(),
    provideFeatureFlags(),
    providePluginExecution(),
    provideAnimationsAsync(),
    provideBDSTheme(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideOktaAuth(),
    provideAppAuth(),
    ClientSearchService,
    ClientSessionService,
    FeedbackService,
    provideCopilot(),
    provideFinancialSnapshot(),
    provideCreditOverview(),
    provideAccounts(),
    provideReviewTab(),
    provideOpenActions(),
    provideCarePlan(),
    {
      provide: ACTION_EMPLOYEE_SEARCH_API,
      useExisting: EmployeeSearchService,
    },
    {
      provide: CARE_PLAN_NAVI,
      useExisting: CopilotFacadeService,
    },
    {
      provide: CARE_PLAN_ACCESS,
      useFactory: () => {
        const auth = inject(AuthService);
        const tabAccess = inject(TabViewAccess);
        const hasLowerEnvironmentAdminAccess = () =>
          environment.environment !== 'prod' &&
          auth.isInGroup(CARE_PLAN_LOWER_ENV_ADMIN_GROUP);
        return {
          canView: () => tabAccess.canView('care-plan'),
          canEdit: () =>
            auth.isInGroup(CARE_PLAN_EDIT_GROUP) ||
            hasLowerEnvironmentAdminAccess(),
          canOverrideOptionQualification: () =>
            canOverrideOptionQualification(environment.environment, (group) =>
              auth.isInGroup(group),
            ),
        };
      },
    },
    {
      provide: CARE_PLAN_CURRENT_USER,
      useFactory: () => {
        const auth = inject(AuthService);
        return {
          id: computed(() => {
            const subject =
              auth.user()?.sub ?? auth.getDecodedAccessToken()?.['sub'];
            return typeof subject === 'string' && subject.trim()
              ? subject.trim()
              : null;
          }),
          displayName: auth.userDisplayName,
        };
      },
    },
    {
      provide: CARE_PLAN_CLIENT_CONTEXT,
      useFactory: () => {
        const metadata = inject(MemberMetadataHeaderService);
        return {
          activeGuid: metadata.headerGuid,
          clientId: metadata.clientId,
          error: metadata.error,
          refresh: () => {
            const guid = metadata.activeGuid();
            if (guid) metadata.load(guid, { force: true });
          },
        };
      },
    },
  ],
};
